import { IRowNode } from "ag-grid-community";
import { format } from "date-fns";
import React from "react";

import { Chip, MultipleSelectInput } from "@performyard/py-components";
import { ReviewDetailsFrontend } from "@performyard/service-contracts";

import { FilterFormSchema } from "./types";

/**
 * Check if the filter value is valid, and not empty.
 */
export const isValidValue = (value: FilterFormSchema[keyof FilterFormSchema]) => {
	return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== "";
};

/**
 * Check if the row data passes all the active filters.
 *
 * If any filter does not match the row data, short circuit and return false.
 */
export const doesFilterPass = (rowNode: IRowNode<ReviewDetailsFrontend>, activeFilters: Partial<FilterFormSchema>) => {
	if (!rowNode.data) return false;

	const rowData = rowNode.data;

	if (activeFilters.search) {
		if (!rowData.subject?.name?.toLowerCase().includes(activeFilters.search.toLowerCase())) {
			return false;
		}
	}

	if (activeFilters.groupIds) {
		if (!rowData.subjectGroup._id) {
			return false;
		}

		if (!activeFilters.groupIds.includes(rowData.subjectGroup._id)) {
			return false;
		}
	}

	if (activeFilters.dueDateBefore) {
		if (!rowData.reviewDueDate) {
			return false;
		}
		if (rowData.reviewDueDate > activeFilters.dueDateBefore) {
			return false;
		}
	}

	if (activeFilters.dueDateAfter) {
		if (!rowData.reviewDueDate) {
			return false;
		}
		if (rowData.reviewDueDate < activeFilters.dueDateAfter) {
			return false;
		}
	}

	if (activeFilters.unassignedForms) {
		if (rowData.unassignedFormsCount === 0) {
			if (!activeFilters.unassignedForms.includes("none")) {
				return false;
			}
		} else {
			if (!activeFilters.unassignedForms.includes("some")) {
				return false;
			}
		}
	}

	if (activeFilters.externalForms) {
		if (rowData.externalFormsCount === 0) {
			if (!activeFilters.externalForms.includes("none")) {
				return false;
			}
		} else {
			if (!activeFilters.externalForms.includes("some")) {
				return false;
			}
		}
	}

	if (activeFilters.awaitingApprovalForms) {
		if (rowData.pendingThreeSixtyFormsCount === 0) {
			if (!activeFilters.awaitingApprovalForms.includes("none")) {
				return false;
			}
		} else {
			if (!activeFilters.awaitingApprovalForms.includes("some")) {
				return false;
			}
		}
	}

	if (activeFilters.authorProgress) {
		if (rowData.authorProgressPercent === 0) {
			if (!activeFilters.authorProgress.includes("none")) {
				return false;
			}
		} else if (rowData.authorProgressPercent === 1) {
			// Some includes 100%
			if (!activeFilters.authorProgress.includes("completed") && !activeFilters.authorProgress.includes("some")) {
				return false;
			}
		} else {
			if (!activeFilters.authorProgress.includes("some")) {
				return false;
			}
		}
	}

	if (activeFilters.signerProgress) {
		if (rowData.signerProgressPercent === 0) {
			if (!activeFilters.signerProgress.includes("none")) {
				return false;
			}
		} else if (rowData.signerProgressPercent === 1) {
			// Some includes 100%
			if (!activeFilters.signerProgress.includes("completed") && !activeFilters.signerProgress.includes("some")) {
				return false;
			}
		} else {
			if (!activeFilters.signerProgress.includes("some")) {
				return false;
			}
		}
	}

	if (activeFilters.overallProgress) {
		if (rowData.totalProgressPercent === 0) {
			if (!activeFilters.overallProgress.includes("none")) {
				return false;
			}
		} else if (rowData.totalProgressPercent === 1) {
			// Some includes 100%
			if (
				!activeFilters.overallProgress.includes("completed") &&
				!activeFilters.overallProgress.includes("some")
			) {
				return false;
			}
		} else {
			if (!activeFilters.overallProgress.includes("some")) {
				return false;
			}
		}
	}

	return true;
};

const getUnassignedFormOptionLabel = (option: "some" | "none") => {
	return option === "some" ? "Only Unassigned" : "Fully Assigned";
};

const getExternalFormOptionLabel = (option: "some" | "none") => {
	return option === "some" ? "With External Forms" : "Without External Forms";
};

const getAwaitingApprovalFormOptionLabel = (option: "some" | "none") => {
	return option === "some" ? "Pending Approvals" : "Without Pending Approvals";
};

const getProgressOptionLabel = (option: "none" | "some" | "completed") => {
	if (option === "none") {
		return "No Progress";
	}
	if (option === "some") {
		return "Some Progress";
	}
	if (option === "completed") {
		return "Completed";
	}
};

/**
 * Get the parameters for the chips for the given filter key.
 *
 * @param key - The key of the filter.
 * @param params - All applied filter params.
 * @param subjectGroupOptions - The available options for the subject group.
 * @param removeArrayValue - The function to remove a value from the array.
 *
 * @returns An array of parameters for the chips.
 */
export const getChipParams = <T extends keyof FilterFormSchema>(
	key: T,
	params: FilterFormSchema,
	subjectGroupOptions: Parameters<typeof MultipleSelectInput<string>>[0]["options"],
	removeArrayValue: (key: T, value: string) => void
): { label: string; onDismiss?: () => void; key?: string }[] => {
	switch (key) {
		case "groupIds": {
			const groupOptions = subjectGroupOptions.filter((option) => params.groupIds.includes(option.value));

			return groupOptions.map((option) => ({
				label: `Group: ${option.label}`,
				onDismiss: () => removeArrayValue(key, option.value),
				key: `${key}-${option.value}`,
			}));
		}
		case "dueDateBefore": {
			return [
				{
					label: `Due Date Before: ${format(params.dueDateBefore as Date, "MMM d, yyyy")}`,
				},
			];
		}
		case "dueDateAfter": {
			return [
				{
					label: `Due Date After: ${format(params.dueDateAfter as Date, "MMM d, yyyy")}`,
				},
			];
		}
		case "unassignedForms": {
			return params.unassignedForms.map((option) => ({
				label: `Unassigned Forms: ${getUnassignedFormOptionLabel(option)}`,
				onDismiss: () => removeArrayValue(key, option),
				key: `${key}-${option}`,
			}));
		}
		case "externalForms": {
			return params.externalForms.map((option) => ({
				label: `External Forms: ${getExternalFormOptionLabel(option)}`,
				onDismiss: () => removeArrayValue(key, option),
				key: `${key}-${option}`,
			}));
		}
		case "awaitingApprovalForms": {
			return params.awaitingApprovalForms.map((option) => ({
				label: `360 Forms: ${getAwaitingApprovalFormOptionLabel(option)}`,
				onDismiss: () => removeArrayValue(key, option),
				key: `${key}-${option}`,
			}));
		}
		case "authorProgress": {
			return params.authorProgress.map((option) => ({
				label: `Author Progress: ${getProgressOptionLabel(option)}`,
				onDismiss: () => removeArrayValue(key, option),
				key: `${key}-${option}`,
			}));
		}
		case "signerProgress": {
			return params.signerProgress.map((option) => ({
				label: `Signer Progress: ${getProgressOptionLabel(option)}`,
				onDismiss: () => removeArrayValue(key, option),
				key: `${key}-${option}`,
			}));
		}
		case "overallProgress": {
			return params.overallProgress.map((option) => ({
				label: `Overall Progress: ${getProgressOptionLabel(option)}`,
				onDismiss: () => removeArrayValue(key, option),
				key: `${key}-${option}`,
			}));
		}
		default:
			return [];
	}
};

/**
 * Generate the Chip components for the applied filters.
 *
 * @param queryParams - All applied filter params.
 * @param subjectGroupOptions - The available options for the subject group.
 * @param removeArrayValue - Function to remove a value from the array.
 * @param handleQueryChange - Function to handle the query change.
 *
 * @returns An array of Chip components.
 */
export const generateChips = (
	queryParams: FilterFormSchema,
	subjectGroupOptions: Parameters<typeof MultipleSelectInput<string>>[0]["options"],
	removeArrayValue: (key: keyof FilterFormSchema, value: string) => void,
	handleQueryChange: (queryParams: Partial<FilterFormSchema>) => void
) => {
	const chips: React.ReactNode[] = [];

	for (const [key, value] of Object.entries(queryParams)) {
		// Skip if the value is an empty array or null
		if (!isValidValue(value)) {
			continue;
		}

		const chipParams = getChipParams(
			key as keyof FilterFormSchema,
			queryParams,
			subjectGroupOptions,
			removeArrayValue
		);

		for (const chipParam of chipParams) {
			chips.push(
				<Chip
					label={chipParam.label}
					key={chipParam.key ?? `${key}-${value}`}
					color="neutral"
					size={28}
					onDismiss={chipParam.onDismiss ?? (() => handleQueryChange({ [key]: null }))}
				/>
			);
		}
	}

	return chips;
};
